import { List, useTable } from '@refinedev/antd';
import { useList } from '@refinedev/core';
import { Table, Select, Space, Tag, Button, Input, Card, Statistic, Row, Col, DatePicker } from 'antd';
import {
  EyeOutlined,
  EnvironmentOutlined,
  CarOutlined,
  TeamOutlined,
  RocketOutlined,
  CheckCircleOutlined,
  StopOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import type { Dayjs } from 'dayjs';
import { apiClient } from '../../lib/api-client';
import { t } from '../../i18n';

const { RangePicker } = DatePicker;

interface RegionOption {
  id: number;
  nameUz: string;
}

interface DriverPostedTrip {
  id: number;
  tripMode: 'SHAHARLAR_ARO' | 'VILOYATLAR_ARO';
  status: string;
  totalSeats: number;
  availableSeats: number;
  pricePerSeat: number;
  departureTime: string;
  // When the driver posted the trip without selecting a time ("Birinchi safar"),
  // backend stores scheduledTime = null and synthesises departureTime. We render
  // the "Birinchi safar" label only when scheduledTime is null.
  scheduledTime: string | null;
  departureAddress: string;
  destinationAddress: string;
  acceptsRoadside: boolean;
  createdAt: string;
  driver: {
    id: number;
    fullName: string;
    phone: string;
  };
  departureRegion: { nameUz: string } | null;
  destinationRegion: { nameUz: string } | null;
  departureDistrict: { nameUz: string } | null;
  destinationDistrict: { nameUz: string } | null;
  departureCity: { nameUz: string } | null;
  destinationCity: { nameUz: string } | null;
  _count: { bookings: number };
}

const tripModeLabels: Record<string, string> = {
  SHAHARLAR_ARO: 'Viloyat ichida',
  VILOYATLAR_ARO: 'Viloyatlar aro',
};

const tripModeColors: Record<string, string> = {
  SHAHARLAR_ARO: 'blue',
  VILOYATLAR_ARO: 'purple',
};

const statusLabels: Record<string, string> = {
  ACTIVE: t('status.active'),
  FULL: t('status.active'),
  DEPARTED: t('status.onTheWay'),
  COMPLETED: t('status.completed'),
  CANCELLED: t('status.cancelled'),
};

const statusColors: Record<string, string> = {
  ACTIVE: 'blue',
  FULL: 'purple',
  DEPARTED: 'orange',
  COMPLETED: 'green',
  CANCELLED: 'red',
};

export const DriverPostedTripList = () => {
  const navigate = useNavigate();
  const [tripModeFilter, setTripModeFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [searchFilter, setSearchFilter] = useState<string | undefined>(undefined);
  const [departureRegionId, setDepartureRegionId] = useState<number | undefined>();
  const [destinationRegionId, setDestinationRegionId] = useState<number | undefined>();
  const [revenueBucket, setRevenueBucket] = useState<'low' | 'mid' | 'high' | undefined>();
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [regions, setRegions] = useState<RegionOption[]>([]);

  // Region picker — only fetched once. The same list feeds both the
  // departure and destination dropdowns since either side can be
  // anything in the network.
  useEffect(() => {
    apiClient
      .get<RegionOption[]>('/admin/regions')
      .then((res) => {
        const arr = Array.isArray(res.data)
          ? res.data
          : (res.data as { data?: RegionOption[] })?.data || [];
        setRegions(arr);
      })
      .catch(() => setRegions([]));
  }, []);

  const dateFilters = useMemo(() => {
    const out: Array<{ field: string; operator: 'gte' | 'lte'; value: string }> = [];
    if (dateRange?.[0]) out.push({ field: 'fromDate', operator: 'gte', value: dateRange[0].startOf('day').toISOString() });
    if (dateRange?.[1]) out.push({ field: 'toDate', operator: 'lte', value: dateRange[1].endOf('day').toISOString() });
    return out;
  }, [dateRange]);

  // Permanent filters shared between the table and the count chips so
  // both update consistently when any filter changes.
  const sharedFilters = [
    ...(tripModeFilter ? [{ field: 'tripMode', operator: 'eq' as const, value: tripModeFilter }] : []),
    ...(searchFilter ? [{ field: 'search', operator: 'eq' as const, value: searchFilter }] : []),
    ...(departureRegionId ? [{ field: 'departureRegionId', operator: 'eq' as const, value: departureRegionId }] : []),
    ...(destinationRegionId ? [{ field: 'destinationRegionId', operator: 'eq' as const, value: destinationRegionId }] : []),
    ...(revenueBucket ? [{ field: 'revenueBucket', operator: 'eq' as const, value: revenueBucket }] : []),
    ...dateFilters,
  ];

  const { tableProps } = useTable<DriverPostedTrip>({
    resource: 'admin/driver-posted-trips',
    syncWithLocation: true,
    pagination: {
      pageSize: 20,
    },
    filters: {
      permanent: [
        ...sharedFilters,
        ...(statusFilter ? [{ field: 'status', operator: 'eq' as const, value: statusFilter }] : []),
      ],
    },
  });

  const countQuery = (status?: string) => ({
    resource: 'admin/driver-posted-trips',
    pagination: { current: 1, pageSize: 1 },
    filters: [
      ...sharedFilters,
      ...(status ? [{ field: 'status', operator: 'eq' as const, value: status }] : []),
    ],
  });

  const { data: totalData, isLoading: isTotalLoading } = useList<DriverPostedTrip>(countQuery());
  const { data: activeData, isLoading: isActiveLoading } = useList<DriverPostedTrip>(countQuery('ACTIVE'));
  const { data: fullData, isLoading: isFullLoading } = useList<DriverPostedTrip>(countQuery('FULL'));
  const { data: departedData, isLoading: isDepartedLoading } = useList<DriverPostedTrip>(countQuery('DEPARTED'));
  const { data: completedData, isLoading: isCompletedLoading } = useList<DriverPostedTrip>(countQuery('COMPLETED'));
  const { data: cancelledData, isLoading: isCancelledLoading } = useList<DriverPostedTrip>(countQuery('CANCELLED'));

  const totalCount = totalData?.total ?? 0;
  const activeCount = activeData?.total ?? 0;
  const fullCount = fullData?.total ?? 0;
  const departedCount = departedData?.total ?? 0;
  const completedCount = completedData?.total ?? 0;
  const cancelledCount = cancelledData?.total ?? 0;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('uz-UZ').format(price) + " so'm";
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('uz-UZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <List
      title={t('nav.driverTrips')}
      headerButtons={() => null}
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card>
            <Statistic
              title="Jami"
              value={totalCount}
              loading={isTotalLoading}
              prefix={<UnorderedListOutlined style={{ color: '#595959' }} />}
              valueStyle={{ color: '#262626' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card>
            <Statistic
              title="Faol"
              value={activeCount}
              loading={isActiveLoading}
              prefix={<CarOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card>
            <Statistic
              title="To'liq"
              value={fullCount}
              loading={isFullLoading}
              prefix={<TeamOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card>
            <Statistic
              title="Yo'lda"
              value={departedCount}
              loading={isDepartedLoading}
              prefix={<RocketOutlined style={{ color: '#fa8c16' }} />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card>
            <Statistic
              title="Yakunlangan"
              value={completedCount}
              loading={isCompletedLoading}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card>
            <Statistic
              title="Bekor qilingan"
              value={cancelledCount}
              loading={isCancelledLoading}
              prefix={<StopOutlined style={{ color: '#f5222d' }} />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="Rejim"
          allowClear
          style={{ width: 180 }}
          value={tripModeFilter}
          onChange={(value) => setTripModeFilter(value)}
          options={[
            { value: 'SHAHARLAR_ARO', label: 'Viloyat ichida' },
            { value: 'VILOYATLAR_ARO', label: 'Viloyatlar aro' },
          ]}
        />
        <Select
          placeholder="Holat"
          allowClear
          style={{ width: 180 }}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value)}
          options={[
            { value: 'ACTIVE', label: 'Faol' },
            { value: 'FULL', label: "To'liq" },
            { value: 'DEPARTED', label: "Yo'lda" },
            { value: 'COMPLETED', label: 'Yakunlangan' },
            { value: 'CANCELLED', label: 'Bekor qilingan' },
          ]}
        />
        <Input.Search
          placeholder="Haydovchi nomi/telefoni"
          allowClear
          style={{ width: 250 }}
          onSearch={(value) => setSearchFilter(value || undefined)}
        />
        <RangePicker
          // Sana — list-level date range. Filters by `createdAt`.
          value={dateRange as any}
          onChange={(dates) => setDateRange(dates ? [dates[0], dates[1]] : null)}
          format="DD.MM.YYYY"
          placeholder={['Boshlanish', 'Tugash']}
          allowClear
        />
        <Select
          // Viloyat yo'nalishi — qayerdan
          showSearch
          placeholder="Qayerdan (viloyat)"
          allowClear
          style={{ width: 200 }}
          value={departureRegionId}
          onChange={(v) => setDepartureRegionId(v)}
          optionFilterProp="label"
          options={regions.map((r) => ({ value: r.id, label: r.nameUz }))}
        />
        <Select
          // Viloyat yo'nalishi — qayerga
          showSearch
          placeholder="Qayerga (viloyat)"
          allowClear
          style={{ width: 200 }}
          value={destinationRegionId}
          onChange={(v) => setDestinationRegionId(v)}
          optionFilterProp="label"
          options={regions.map((r) => ({ value: r.id, label: r.nameUz }))}
        />
        <Select
          // Daromad bucket — server matches against `pricePerSeat`.
          placeholder="Daromad"
          allowClear
          style={{ width: 200 }}
          value={revenueBucket}
          onChange={(v) => setRevenueBucket(v as 'low' | 'mid' | 'high' | undefined)}
          options={[
            { value: 'low', label: '< 100 000 so\'m' },
            { value: 'mid', label: '100 000 — 500 000 so\'m' },
            { value: 'high', label: '500 000+ so\'m' },
          ]}
        />
      </Space>

      <Table
        {...tableProps}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        rowKey="id"
        size="small"
      >
        <Table.Column dataIndex="id" title="ID" width={60} />
        <Table.Column
          dataIndex="tripMode"
          title="Rejim"
          width={130}
          render={(mode: string) => (
            <Tag color={tripModeColors[mode] || 'default'}>
              {tripModeLabels[mode] || mode}
            </Tag>
          )}
        />
        <Table.Column
          title="Haydovchi"
          width={180}
          render={(_, record: DriverPostedTrip) => (
            <div>
              <div style={{ fontWeight: 500 }}>{record.driver?.fullName || '-'}</div>
              <div style={{ fontSize: 12, color: '#999' }}>{record.driver?.phone || '-'}</div>
            </div>
          )}
        />
        <Table.Column
          title="Marshrut"
          render={(_, record: DriverPostedTrip) => (
            <Space size={4}>
              <EnvironmentOutlined style={{ color: '#52c41a' }} />
              <span>{record.departureRegion?.nameUz || '-'}</span>
              <span style={{ color: '#999' }}>→</span>
              <EnvironmentOutlined style={{ color: '#f5222d' }} />
              <span>{record.destinationRegion?.nameUz || '-'}</span>
            </Space>
          )}
        />
        <Table.Column
          title="Joylar"
          width={90}
          render={(_, record: DriverPostedTrip) => (
            <Tag color={record.availableSeats > 0 ? 'green' : 'red'}>
              {record.availableSeats}/{record.totalSeats}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="pricePerSeat"
          title="Narx"
          width={120}
          render={(price: number) => formatPrice(price)}
        />
        <Table.Column<DriverPostedTrip>
          dataIndex="departureTime"
          title="Jo'nash vaqti"
          width={150}
          render={(date: string, record) =>
            record.scheduledTime == null ? (
              <Tag color="purple" style={{ margin: 0 }}>Birinchi safar</Tag>
            ) : (
              formatDate(date)
            )
          }
        />
        <Table.Column
          dataIndex="status"
          title="Holat"
          width={130}
          render={(status: string) => (
            <Tag color={statusColors[status] || 'default'}>
              {statusLabels[status] || status}
            </Tag>
          )}
        />
        <Table.Column
          title="Buyurtmalar"
          width={100}
          render={(_, record: DriverPostedTrip) => (
            <Tag>{record._count?.bookings || 0}</Tag>
          )}
        />
        <Table.Column
          title="Amallar"
          width={80}
          render={(_, record: DriverPostedTrip) => (
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/driver-posted-trips/${record.id}`)}
            />
          )}
        />
      </Table>
    </List>
  );
};
